// UniversalForm.tsx
import React, { FC, useState, useContext } from "react";
import { Context } from "../../index";
import { observer } from "mobx-react-lite";

import CustomToastContainer from "../forms/notification/CustomToastContainer";
import { useNotify } from "../../shared/hooks/useNotify";
import FileUpload from "../upload/FileUpload";

import "./styles.css";
import SelectLine from "./selectors/Selector";
import SVG from "../svg/Svg";
import DateRangeInput from "./range/DateRangeInput";

export type FieldType = "text" | "select" | "dateRange";

export interface FieldConfig {
  label: string;
  type?: FieldType;
  options?: Array<{ id: string; name: string }>;
  required?: boolean;
}

interface UniversalFormProps {
  title: string;
  handleCloseModal: () => void;
  mode: "create" | "edit" | "export";
  entityData?: Record<string, string>;
  fields: Record<string, FieldConfig>;
  createEntity: (data: Record<string, string>) => Promise<void>;
  editEntity?: (data: Record<string, string>) => Promise<void>;
  deleteEntity?: () => void | Promise<void>;
  validateDelete?: () => string | undefined;
  entity?: string;
  onFieldChange?: (field: string, value: string) => void;
  submitButtonText?: string;
  withPhotoUpload?: boolean;
  onPhotoUpload?: (photoUrl: string) => void;
  /** `B.12`: дополнительные действия карточки (ключ API) в режиме правки. */
  extraActions?: React.ReactNode;
}

const UniversalForm: FC<UniversalFormProps> = ({
  title,
  handleCloseModal,
  mode,
  entityData = {},
  fields,
  createEntity,
  editEntity,
  deleteEntity,
  validateDelete,
  entity,
  onFieldChange,
  submitButtonText,
  withPhotoUpload,
  onPhotoUpload,
  extraActions,
}) => {
  const { store } = useContext(Context);
  const notify = useNotify();

  const [formData, setFormData] = useState<Record<string, string>>(() => {
    return mode === "edit" && entityData ? entityData : {};
  });

  const handleFieldChange = (key: string, value: string) => {
    setFormData((prevState) => ({ ...prevState, [key]: value }));
    onFieldChange?.(key, value);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);
  };

  const handleSelectChange = (key: string, value: string) => {
    handleFieldChange(key, value);
  };

  const handlDel = () => {
    const validationError = validateDelete?.();
    if (validationError) {
      notify.error(validationError);
      return;
    }

    notify.confirm({
      message: `удалить ${entity} ?`,
      onConfirm: async () => {
        try {
          await deleteEntity?.();
          handleCloseModal();
        } catch (error: any) {
          notify.error(
            error?.response?.data?.message ||
              error?.message ||
              "Ошибка при удалении",
          );
        }
      },
      type: "del",
      title: "удалить",
      autoClose: 3000,
    });
  };

  const isFormValid = () => {
    return Object.entries(fields).every(([key, config]) => {
      if (!config.required) return true;
      const value = formData[key];
      return (
        value !== null && value !== undefined && value.toString().trim() !== ""
      );
    });
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      notify.error("Пожалуйста, заполните все поля");
      return;
    }

    try {
      if (mode === "edit") {
        await editEntity?.(formData);
      } else {
        await createEntity(formData);
      }

      handleCloseModal();
    } catch (error: any) {
      notify.error(error.message || "Произошла ошибка при сохранении");
    }
  };

  const handleSubmitWithFormat = async (format: "excel" | "pdf") => {
    if (!isFormValid()) {
      notify.error("Пожалуйста, заполните все поля");
      return;
    }

    try {
      await createEntity({ ...formData, format });
      handleCloseModal();
    } catch (error: any) {
      notify.error(error.message || "Произошла ошибка при экспорте");
    }
  };

  const renderField = (key: string, config: FieldConfig) => {
    switch (config.type) {
      case "select":
        return (
          <SelectLine
            options={config.options || []}
            value={formData[key] || ""}
            onChange={(value) => handleSelectChange(key, value)}
            required={config.required}
          />
        );
      case "dateRange":
        return (
          <DateRangeInput
            value={formData[key] || ""}
            onChange={(val) => handleFieldChange(key, val)}
          />
        );
      default:
        return (
          <input
            className="CreateForm_form_input"
            id={key}
            name={key}
            type="text"
            value={formData[key] || ""}
            onChange={handleChange}
            required={config.required}
          />
        );
    }
  };

  return (
    <>
      <div className="createForm_container">
        <div className="createForm_close">
          <div onClick={handleCloseModal} className="createForm_close_btn">
            {SVG["close"]}
          </div>
        </div>
        <div className="title_cont">
          <div className="createForm_title">
            {mode === "edit" && entityData ? `${entityData.name}` : title}
          </div>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="CreateForm">
          {mode === "edit" && withPhotoUpload && onPhotoUpload && (
            <div className="photo-upload-section">
              <label className="CreateForm_form_label">Фото</label>
              <FileUpload
                onUpload={async (file: File) => {
                  if (onPhotoUpload) {
                    onPhotoUpload(file);
                  }
                }}
              >
                <div className="photo-upload-trigger">
                  {SVG["add_img"]} Загрузить фото
                </div>
              </FileUpload>
            </div>
          )}

          {Object.entries(fields).map(([key, config]) => {
            if (mode === "edit" && config.type === "select") {
              return null;
            }

            return (
              <div className="CreateForm_form" key={key}>
                <label className="CreateForm_form_label" htmlFor={key}>
                  {config.label}
                  {config.required ? (
                    <span style={{ color: "red" }}>*</span>
                  ) : null}
                </label>
                {renderField(key, config)}
              </div>
            );
          })}

          {mode === "edit" && extraActions ? (
            <div className="createForm_extra">{extraActions}</div>
          ) : null}

          {mode === "export" ? (
            <div className="exportBtnGroup">
              <div
                className="createForm_btn"
                onClick={() => handleSubmitWithFormat("excel")}
              >
                Экспорт в Excel
              </div>
              <div
                className="createForm_btn"
                onClick={() => handleSubmitWithFormat("pdf")}
              >
                Экспорт в PDF
              </div>
            </div>
          ) : mode === "edit" ? (
            <>
              <div className="createForm_btn" onClick={handleSubmit}>
                {submitButtonText || "Сохранить"}
              </div>
              {store.user.role === "root" && deleteEntity && (
                <div className="deleteForm_btn" onClick={handlDel}>
                  удалить
                </div>
              )}
            </>
          ) : (
            <div className="createForm_btn" onClick={handleSubmit}>
              {submitButtonText || "Добавить"}
            </div>
          )}
        </form>
      </div>
      <CustomToastContainer />
    </>
  );
};

export default observer(UniversalForm);
